import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, TrendingUp, Wallet, Hourglass, Landmark } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { MetricCard, PageSkeleton } from "@/components/studio/StudioUI";
import { AccountingExport } from "@/components/studio/AccountingExport";
import { AccountantCard } from "@/components/studio/AccountantCard";
import { VerifactuCard } from "@/components/studio/VerifactuCard";
import { EinvoiceCard } from "@/components/studio/EinvoiceCard";
import { studio, apiError, eur, btnGhost, inputCls } from "@/lib/studioApi";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const MONTHS_LONG = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function StudioFinances() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const fiscalCountry = data?.country;
  const [exporting, setExporting] = useState(false);
  const [sentCount, setSentCount] = useState(0);

  useEffect(() => {
    setData(null);
    studio.get(`/finances?year=${year}`).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiError(e, "Chargement impossible.")));
  }, [year]);

  const max = Math.max(1, ...(data?.months || []).map((m) => Math.max(m.invoiced_total, m.collected)));
  const now = new Date();
  const currentMonth = year === now.getFullYear() ? now.getMonth() : 11;

  return (
    <StudioLayout
      title="Finances"
      subtitle="Facturé, encaissé, à encaisser et TVA due — mois par mois."
      ownerOnly
      action={
        <div className="flex items-center gap-2">
          <select data-testid="finances-year" className={`${inputCls} w-28`} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {(data?.years?.length ? data.years : [year]).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button type="button" data-testid="finances-export" onClick={() => setExporting(true)} className={`${btnGhost} whitespace-nowrap`}>
            <Download className="h-4 w-4" /> Export comptable
          </button>
        </div>
      }
    >
      {exporting && <AccountingExport onClose={() => setExporting(false)} onSent={() => setSentCount((n) => n + 1)} />}
      {!data ? (
        <PageSkeleton />
      ) : (
        <>
          <div data-testid="finances-kpis" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricCard testId="fin-invoiced" icon={TrendingUp} tone="blue" label={`Facturé ${year} TVAC`} value={eur(data.totals.invoiced_total)} hint={`${eur(data.totals.invoiced_net)} HTVA`} />
            <MetricCard testId="fin-collected" icon={Wallet} tone="emerald" label="Encaissé" value={eur(data.totals.collected)} hint="paiements reçus cette année" delay={60} />
            <MetricCard testId="fin-outstanding" icon={Hourglass} tone="amber" label="À encaisser" value={eur(data.totals.outstanding)} hint={data.totals.overdue ? `dont ${eur(data.totals.overdue)} en retard` : "aucun retard"} to="/studio/factures" delay={120} />
            <MetricCard testId="fin-vat" icon={Landmark} tone="slate" label="TVA facturée" value={eur(data.totals.invoiced_vat)} hint="à reverser (déclarations)" delay={180} />
          </div>

          <div data-testid="finances-chart" className="dv-card-in mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-heading text-base font-bold text-[#0F2645]">Activité mensuelle</p>
              <p className="flex items-center gap-4 text-xs text-[#5C6A87]">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#0F2645]" /> Facturé TVAC</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#3A5CC8]" /> Encaissé</span>
              </p>
            </div>
            <div className="mt-5 grid grid-cols-12 items-end gap-1.5 sm:gap-3" style={{ height: 180 }}>
              {data.months.map((m, i) => (
                <div key={m.month} data-testid={`fin-month-${m.month}`} className="group relative flex h-full flex-col justify-end" title={`${MONTHS_LONG[i]} : facturé ${eur(m.invoiced_total)} · encaissé ${eur(m.collected)}`}>
                  <div className="flex h-full items-end justify-center gap-[2px] sm:gap-1">
                    <span className={`w-full max-w-[14px] rounded-t-sm transition-all duration-500 ${i > currentMonth ? "bg-slate-200" : "bg-[#0F2645]"}`} style={{ height: `${Math.max(m.invoiced_total > 0 ? 3 : 0, (m.invoiced_total / max) * 100)}%` }} />
                    <span className="w-full max-w-[14px] rounded-t-sm bg-[#3A5CC8] transition-all duration-500" style={{ height: `${Math.max(m.collected > 0 ? 3 : 0, (m.collected / max) * 100)}%` }} />
                  </div>
                  <span className={`mt-1.5 block text-center text-[10px] font-semibold ${i === currentMonth ? "text-[#3A5CC8]" : "text-[#93A0BC]"}`}>{MONTHS[i]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div data-testid="finances-table" className="dv-card-in overflow-x-auto rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
              <table className="w-full min-w-[420px] text-sm">
                <thead className="bg-[#F6F8FD] text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">
                  <tr><th className="px-4 py-2.5 text-left">Mois</th><th className="px-3 py-2.5 text-right">Factures</th><th className="px-3 py-2.5 text-right">Facturé TVAC</th><th className="hidden px-3 py-2.5 text-right sm:table-cell">TVA</th><th className="px-4 py-2.5 text-right">Encaissé</th></tr>
                </thead>
                <tbody>
                  {data.months.filter((m) => m.count || m.collected).map((m) => (
                    <tr key={m.month} data-testid={`fin-row-${m.month}`} className="border-t border-[#E2E7F7]">
                      <td className="px-4 py-2.5 font-semibold text-[#0F2645]">{MONTHS_LONG[m.month - 1]}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-[#5C6A87]">{m.count}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-[#0F2645]">{eur(m.invoiced_total)}</td>
                      <td className="hidden px-3 py-2.5 text-right font-mono tabular-nums text-[#5C6A87] sm:table-cell">{eur(m.invoiced_vat)}</td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums font-semibold text-[#3A5CC8]">{eur(m.collected)}</td>
                    </tr>
                  ))}
                  {!data.months.some((m) => m.count || m.collected) && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#5C6A87]">Aucune facture en {year}. <Link to="/studio/factures" className="font-semibold text-[#3A5CC8] hover:underline">Créer une facture</Link></td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div data-testid="finances-vat" className="dv-navy dv-card-in rounded-2xl p-5 text-white shadow-[0_18px_40px_-24px_rgba(8,16,77,0.7)]">
              <p className="font-heading text-base font-bold">{data.vat_period === "monthly" ? "TVA par mois" : "TVA par trimestre"}</p>
              <p className="mt-1 text-xs text-[#9FB4F2]">TVA facturée à déclarer — déduisez votre TVA sur achats avec votre comptable.</p>
              {data.declaration?.text && <p data-testid="finances-vat-regime" className="mt-1 text-xs font-semibold text-white/80">{data.declaration.text}</p>}
              <div className="mt-4 space-y-2">
                {data.vat_period === "monthly" ? data.months.filter((m) => m.invoiced_total > 0 || year < new Date().getFullYear() || m.month <= new Date().getMonth() + 1).map((m) => (
                  <div key={m.month} data-testid={`fin-month-${m.month}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.07] px-4 py-2.5">
                    <span className="text-sm font-semibold capitalize">{new Date(year, m.month - 1, 1).toLocaleDateString("fr-BE", { month: "long" })} {year} <span className="ml-1 text-xs normal-case text-[#9FB4F2]">({eur(m.invoiced_total)} facturés)</span></span>
                    <span className="font-mono text-base font-bold tabular-nums">{eur(m.invoiced_vat)}</span>
                  </div>
                )) : data.quarters.map((q) => (
                  <div key={q.quarter} data-testid={`fin-quarter-${q.quarter}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.07] px-4 py-3">
                    <span className="text-sm font-semibold">T{q.quarter} {year} <span className="ml-1 text-xs text-[#9FB4F2]">({eur(q.invoiced_total)} facturés)</span></span>
                    <span className="font-mono text-base font-bold tabular-nums">{eur(q.vat_due)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <AccountantCard refreshKey={sentCount} />
          {data?.declaration && fiscalCountry === "ES" && <VerifactuCard />}
          {data?.declaration && fiscalCountry === "FR" && <EinvoiceCard />}
        </>
      )}
    </StudioLayout>
  );
}
